import { Component, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { SigninService } from '../../../signin/signin.service';
import { FloatingSubmenuCoordinatorService } from '../../services/floating-submenu-coordinator.service';
import { ModalController } from '@ionic/angular';
import { FuelDataComponent } from 'src/app/pages/statistical/fuel-data/fuel-data.component';
import { TechnicalDataComponent } from 'src/app/pages/statistical/technical-data/technical-data.component';
import { DataManagementService } from 'src/app/pages/data-management/data-management.service';

@Component({
  selector: 'app-graphical-report-submenu',
  templateUrl: './graphical-report-submenu.component.html',
  styleUrls: ['./graphical-report-submenu.component.scss'],
})
export class GraphicalReportSubmenuComponent implements OnDestroy {
  readonly submenuId = 'graphical-report';

  showSubmenu = false;
  /** Backdrop becomes interactive only after open gesture has finished. */
  backdropEnabled = false;
  submenuLeft = 0;

  private openedAt = 0;
  private backdropTimer: any;
  private coordinatorSubscription: Subscription;

  constructor(
    public signinService: SigninService,
    public dataManagementService: DataManagementService,
    private submenuCoordinator: FloatingSubmenuCoordinatorService,
    private modalController: ModalController,
  ) {
    this.coordinatorSubscription = this.submenuCoordinator.watchActiveSubmenu().subscribe(activeId => {
      if (activeId && activeId !== this.submenuId) {
        this.forceClose(false);
      }
      if (activeId === null && this.showSubmenu && !this.isWithinOpenGuard()) {
        this.forceClose(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.clearBackdropTimer();
    this.coordinatorSubscription?.unsubscribe();
  }

  showFloatingSubmenu(event?: Event) {
    const target = (event?.currentTarget || event?.target) as HTMLElement | undefined;
    if (target?.getBoundingClientRect) {
      const rect = target.getBoundingClientRect();
      this.submenuLeft = rect.left + rect.width / 2;
    }

    this.clearBackdropTimer();
    this.openedAt = Date.now();
    this.backdropEnabled = false;
    this.submenuCoordinator.open(this.submenuId);
    this.showSubmenu = true;

    this.backdropTimer = setTimeout(() => {
      this.backdropEnabled = true;
    }, 450);
  }

  toggleFloatingSubmenu(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.showSubmenu) {
      if (this.isWithinOpenGuard()) {
        return;
      }
      this.hideFloatingSubmenu(true);
      return;
    }

    this.showFloatingSubmenu(event);
  }

  onBackdropClick(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.backdropEnabled || this.isWithinOpenGuard()) {
      return;
    }
    this.hideFloatingSubmenu(true);
  }

  hideFloatingSubmenu(immediate = false, notifyCoordinator = true) {
    this.forceClose(notifyCoordinator);
  }

  async openCarburantModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: FuelDataComponent,
      cssClass: 'fuel-overlay-modal',
      backdropDismiss: true,
      showBackdrop: true,
      componentProps: {
        overlayOnly: true,
      },
    });
    await modal.present();
  }

  async openTechnicalModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: TechnicalDataComponent,
      cssClass: 'fuel-overlay-modal',
      backdropDismiss: true,
      showBackdrop: true,
      componentProps: {
        overlayOnly: true,
      },
    });
    await modal.present();
  }

  canShowFuelReport(): boolean {
    return this.signinService.hasVehicleFuelOption(this.dataManagementService.selectedVehicule);
  }

  canShowTemperatureReport(): boolean {
    return this.signinService.hasVehicleTemperatureOption(this.dataManagementService.selectedVehicule);
  }

  private isWithinOpenGuard(): boolean {
    return Date.now() - this.openedAt < 800;
  }

  private forceClose(notifyCoordinator: boolean) {
    this.clearBackdropTimer();
    this.showSubmenu = false;
    this.backdropEnabled = false;
    if (notifyCoordinator) {
      this.submenuCoordinator.closeAll();
    }
  }

  private clearBackdropTimer() {
    if (this.backdropTimer) {
      clearTimeout(this.backdropTimer);
      this.backdropTimer = null;
    }
  }
}
